import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { hasRole, hasBridgeAccess } from '../../utils/permissions';
import {
  getPlanningSectionHomePath,
  getVisiblePlanningNavItems,
  isPlanningNavItemVisible,
  type PlanningNavItem,
} from '../../config/planningNav';
import {
  canAccessAdminNav,
  getAdminNavDropdownEntries,
  getAdminSectionHomePath,
  isAdminNavItemVisible,
  type AdminNavItem,
} from '../../config/adminNav';
import {
  canSeeUsersAdmin,
  canSeeRolesAdmin,
  canSeeUserTypesAdmin,
} from '../../utils/adminSectionAccess';
import styles from './TabNav.module.css';
import { prefetchRoute } from '../../utils/routePrefetch';

export interface NavItem {
  key: string;
  label: string;
  icon?: string;
  path?: string;
  roleKey?: string;
  adminOnly?: boolean;
  adminUsersSection?: boolean;
  adminUserTypesSection?: boolean;
  adminRolesSection?: boolean;
  section?: boolean;
  children?: NavItem[];
  /** When set, label click navigates here instead of toggling the dropdown. */
  sectionHomePath?: string;
}

function navItemMatchesPath(item: NavItem, pathname: string): boolean {
  if (item.section) return false;
  if (item.path) {
    if (item.path === '/' && pathname === '/') return true;
    if (item.path !== '/' && pathname === item.path) return true;
    if (item.path !== '/' && pathname.startsWith(`${item.path}/`)) return true;
    return false;
  }
  if (item.children) {
    return item.children.some((c) => navItemMatchesPath(c, pathname));
  }
  return false;
}

function planningNavChildren(user: ReturnType<typeof useAuth>['currentUser']): NavItem[] {
  return getVisiblePlanningNavItems(user).map((item: PlanningNavItem) => ({
    key: item.key,
    label: item.label,
    icon: item.icon,
    path: item.path,
    roleKey: item.roleKey,
  }));
}

function adminNavChildren(user: ReturnType<typeof useAuth>['currentUser']): NavItem[] {
  return getAdminNavDropdownEntries(user).map((entry) => {
    if (entry.type === 'section') {
      return { key: entry.key, label: entry.label, section: true };
    }
    const item: AdminNavItem = entry.item;
    return {
      key: item.key,
      label: item.label,
      icon: item.icon,
      path: item.path,
      roleKey: item.roleKey,
      adminUsersSection: item.adminUsersSection,
      adminUserTypesSection: item.adminUserTypesSection,
      adminRolesSection: item.adminRolesSection,
    };
  });
}

const STATIC_NAV_ITEMS: NavItem[] = [
  { key: 'dashboard', label: 'Dashboard', path: '/', icon: 'mountain' },
  // DISABLED: Current Expense nav group hidden (see docs/DISABLED_FEATURES.md)
  {
    key: 'selfService', label: 'Self Service', children: [
      // DISABLED: My Tickets (see docs/DISABLED_FEATURES.md)
    ],
  },
];

export default function TabNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const navRef = useRef<HTMLDivElement>(null);

  const navItems = useMemo((): NavItem[] => {
    const planningChildren = planningNavChildren(currentUser);
    const adminChildren = adminNavChildren(currentUser);
    return [
      ...STATIC_NAV_ITEMS,
      {
        key: 'budgetPlan',
        label: 'Budget Planning',
        sectionHomePath: getPlanningSectionHomePath(currentUser),
        children: planningChildren,
      },
      {
        key: 'admin',
        label: 'Admin',
        sectionHomePath: getAdminSectionHomePath(currentUser),
        children: adminChildren,
      },
      { key: 'help', label: 'Help', path: '/help', icon: 'circle-help' },
    ];
  }, [currentUser]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    }
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // eslint-disable-next-line react-hooks/set-state-in-effect -- sync UI to navigation; no external subscription API
  useEffect(() => { setOpenDropdown(null); }, [location.pathname]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpenDropdown(null);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  function isVisible(item: NavItem): boolean {
    if (item.key === 'admin') return canAccessAdminNav(currentUser);
    if (item.adminOnly && !currentUser?.is_admin) return false;
    if (item.roleKey && !hasRole(currentUser, item.roleKey)) return false;
    if (item.key === 'bridges' && !hasBridgeAccess(currentUser)) return false;
    if (item.adminUsersSection && !canSeeUsersAdmin(currentUser)) return false;
    if (item.adminUserTypesSection && !canSeeUserTypesAdmin(currentUser)) return false;
    if (item.adminRolesSection && !canSeeRolesAdmin(currentUser)) return false;
    if (item.children && item.children.filter(isVisible).length === 0) return false;
    return true;
  }

  function isAdminDropdownChildVisible(child: NavItem): boolean {
    if (child.section) return true;
    if (!child.path) return false;
    const adminItem: AdminNavItem = {
      key: child.key,
      label: child.label,
      path: child.path,
      icon: child.icon ?? '',
      roleKey: child.roleKey,
      adminUsersSection: child.adminUsersSection,
      adminUserTypesSection: child.adminUserTypesSection,
      adminRolesSection: child.adminRolesSection,
    };
    return isAdminNavItemVisible(currentUser, adminItem);
  }

  function visibleChildren(item: NavItem): NavItem[] {
    if (!item.children?.length) return [];
    if (item.key === 'admin') return item.children.filter(isAdminDropdownChildVisible);
    if (item.key === 'budgetPlan') {
      return item.children.filter((child) => {
        const planningItem = getVisiblePlanningNavItems(currentUser).find((p) => p.key === child.key);
        return planningItem ? isPlanningNavItemVisible(currentUser, planningItem) : false;
      });
    }
    return item.children.filter(isVisible);
  }

  function isTabActive(item: NavItem): boolean {
    if (item.sectionHomePath && location.pathname === item.sectionHomePath) return true;
    if (item.path) return navItemMatchesPath(item, location.pathname);
    if (item.children) {
      return visibleChildren(item).some((c) => navItemMatchesPath(c, location.pathname));
    }
    return false;
  }

  function goTo(path: string) {
    prefetchRoute(path);
    if (path === '/help') {
      navigate(path, { state: { from: location.pathname } });
    } else {
      navigate(path);
    }
    setOpenDropdown(null);
  }

  function handleTabLabelClick(item: NavItem) {
    if (item.sectionHomePath) {
      goTo(item.sectionHomePath);
      return;
    }
    if (item.path) {
      goTo(item.path);
    }
  }

  function handleTabChevronClick(item: NavItem, e: React.MouseEvent) {
    e.stopPropagation();
    setOpenDropdown(openDropdown === item.key ? null : item.key);
  }

  function handleItemClick(child: NavItem) {
    if (child.section) return;
    if (child.path) {
      goTo(child.path);
    }
  }

  function renderTabButton(item: NavItem) {
    const active = isTabActive(item);
    const hasSplit = Boolean(item.children?.length && item.sectionHomePath);

    if (hasSplit) {
      return (
        <div className={`${styles.tabSplit} ${active ? styles.active : ''}`}>
          <button
            type="button"
            className={styles.tabLabel}
            onMouseEnter={() => item.sectionHomePath && prefetchRoute(item.sectionHomePath)}
            onClick={() => handleTabLabelClick(item)}
          >
            {item.label}
          </button>
          <button
            type="button"
            className={styles.tabChevron}
            aria-label={`Open ${item.label} menu`}
            aria-expanded={openDropdown === item.key}
            aria-haspopup="true"
            onClick={(e) => handleTabChevronClick(item, e)}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>
        </div>
      );
    }

    return (
      <button
        type="button"
        className={`${styles.tab} ${active ? styles.active : ''}`}
        onMouseEnter={() => item.path && prefetchRoute(item.path)}
        onClick={() => handleTabLabelClick(item)}
        {...(item.children ? { 'aria-expanded': openDropdown === item.key, 'aria-haspopup': 'true' as const } : {})}
      >
        {item.label}
        {item.children && (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft: 4 }} aria-hidden="true">
            <path d="m6 9 6 6 6-6" />
          </svg>
        )}
      </button>
    );
  }

  return (
    <div ref={navRef} className={styles.container}>
      <nav className={styles.tabs} role="navigation" aria-label="Main navigation">
        {navItems.filter(isVisible).map((item) => (
          <div key={item.key} className={styles.tabWrapper}>
            {renderTabButton(item)}

            {item.children && openDropdown === item.key && (
              <div className={`${styles.dropdown} ${item.key === 'admin' ? styles.megaDropdown : styles.compactDropdown}`}>
                <div className={styles.dropdownContent} role="menu">
                  {visibleChildren(item).map((child) => (
                    child.section ? (
                      <div
                        key={child.key}
                        role="presentation"
                        className={styles.dropdownSectionLabel}
                      >
                        {child.label}
                      </div>
                    ) : (
                      <button
                        type="button"
                        key={child.key}
                        role="menuitem"
                        className={`${styles.dropdownItem} ${child.path && navItemMatchesPath(child, location.pathname) ? styles.dropdownItemActive : ''}`}
                        onMouseEnter={() => child.path && prefetchRoute(child.path)}
                        onClick={() => handleItemClick(child)}
                      >
                        {child.label}
                      </button>
                    )
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </nav>
    </div>
  );
}
